<template>
  <div class="fullscreen-video-container">
    <div v-if="currentVideo" class="video-wrapper">
      <video
        ref="videoPlayer"
        preload="none"
        autoplay
        muted
        playsinline
        :loop="shouldLoop"
        class="video-player"
        @loadeddata="onVideoLoaded"
        @canplaythrough="onVideoCanPlayThrough"
        @ended="onVideoEnded"
        @timeupdate="onVideoTimeUpdate"
        @error="onVideoError"
        @loadstart="onVideoLoadStart"
      >
        您的浏览器不支持视频播放
      </video>
    </div>
    <div v-else class="no-video-message">
      暂无视频
    </div>
  </div>
</template>

<script>
import config from '@/config';
import logger from '@/utils/logger';

export default {
  name: 'SleepViewVideo',
  props: {
    videoList: {
      type: Array,
      default: () => []
    },
    currentVideoIndex: {
      type: Number,
      default: 0
    },
    preloadedVideo: {
      type: HTMLVideoElement,
      default: null
    },
    // 父组件传入的断点（秒）。刷新/重启不记忆；同一次运行内，轮播切走再回来会用到。
    resumeTime: {
      type: Number,
      default: 0
    },
    // 视频切换模式：
    // - FIXED_WINDOW：按时间窗口循环播放（当前页面停留时间内，视频不断循环）
    // - PLAY_TO_END：自然播放到结尾后再由父组件决定切换逻辑
    videoSwitchMode: {
      type: String,
      default: 'FIXED_WINDOW'
    }
  },
  computed: {
    currentVideo() {
      if (!this.videoList || this.videoList.length === 0) {
        return null;
      }
      const index = this.currentVideoIndex % this.videoList.length;
      return this.videoList[index];
    },
    currentVideoId() {
      // 获取当前视频ID，用于缓存URL
      return this.currentVideo ? this.currentVideo.id : null;
    },
    // 是否对 <video> 启用 loop。只在 FIXED_WINDOW 模式下开启，
    // 这样在本页停留的时间窗口内，视频会自动循环播放，不会停在结束画面。
    shouldLoop() {
      return this.videoSwitchMode === 'FIXED_WINDOW';
    }
  },
  data() {
    return {
      playRetryCount: 0, // 播放重试次数
      maxRetryCount: 3, // 最大重试次数
      isPlaying: false, // 是否正在播放
      videoUrlCache: {}, // 视频URL缓存，key为videoId，value为URL
      currentVideoElementSrc: '', // 当前视频元素的实际src，避免重复设置
      checkReadyTimer: null, // 播放准备就绪轮询定时器，销毁时清理
      retryPlayTimer: null, // 播放失败重试定时器，销毁时清理
      resumeAppliedForVideoId: null // 记录已应用断点的视频ID，避免重复设置 currentTime 导致抖动
    };
  },
  mounted() {
    // 大屏展示：进入视频页后自动播放（是否展示由后端/前端的“启用视频过滤”决定）
    this.$nextTick(() => {
      this.updateVideoSrc();
      this.attemptPlay();
    });
  },
  watch: {
    // 监听当前视频ID变化，确保只在真正变化时才更新
    currentVideoId(newId, oldId) {
      if (newId !== oldId) {
        // 视频ID变化时，重置播放状态并更新src
        this.isPlaying = false;
        this.playRetryCount = 0;
        this.resumeAppliedForVideoId = null;
        this.updateVideoSrc();
        // 切换视频后继续自动播放
        this.$nextTick(() => {
          this.attemptPlay();
        });
      }
    },
    // 监听preloadedVideo变化，只在真正变化时才更新
    preloadedVideo(newVideo, oldVideo) {
      // 只在preloadedVideo真正变化时才更新
      if (newVideo !== oldVideo) {
        this.updateVideoSrc();
      }
    }
  },
  beforeDestroy() {
    if (this.checkReadyTimer) {
      clearTimeout(this.checkReadyTimer);
      this.checkReadyTimer = null;
    }
    if (this.retryPlayTimer) {
      clearTimeout(this.retryPlayTimer);
      this.retryPlayTimer = null;
    }
    if (this.isPlaying) {
      this.$emit('video-playing-changed', false);
    }
  },
  methods: {
    applyResumeTimeIfNeeded() {
      if (!this.$refs.videoPlayer) return;
      if (!this.currentVideoId) return;
      if (this.resumeAppliedForVideoId === this.currentVideoId) return;

      const video = this.$refs.videoPlayer;
      const t = typeof this.resumeTime === 'number' && isFinite(this.resumeTime) && this.resumeTime > 0
        ? this.resumeTime
        : 0;

      try {
        // duration 可能还不可用，先粗略设置；如果超出范围，浏览器会自动夹逼或抛错
        if (t > 0) {
          video.currentTime = t;
        }
        this.resumeAppliedForVideoId = this.currentVideoId;
      } catch (e) {
        // 某些浏览器/状态下设置 currentTime 会失败，忽略即可
      }
    },
    getVideoUrl(video) {
      if (!video) return '';
      
      // 优先使用后端返回并已拼接好的 url 字段
      if (video.url) {
        return video.url;
      }
      
      // 兼容旧数据：如果没有 url，但有 id，则根据 id 生成播放地址
      if (video.id) {
        return config.getApiUrl(config.apiEndpoints.welcomeVideo.get, video.id);
      }
      
      return '';
    },
    // 获取缓存的视频URL，避免重复生成；限制缓存条数防止内存无限增长
    getCachedVideoUrl(video) {
      if (!video || !video.id) return '';
      
      // 如果缓存中有，直接返回
      if (this.videoUrlCache[video.id]) {
        return this.videoUrlCache[video.id];
      }
      
      const maxCacheSize = 20;
      const keys = Object.keys(this.videoUrlCache);
      if (keys.length >= maxCacheSize) {
        // 删掉最旧的一条（Object.keys 顺序为插入顺序）
        const oldestKey = keys[0];
        this.$delete(this.videoUrlCache, oldestKey);
      }
      
      // 生成URL并缓存
      const url = this.getVideoUrl(video);
      if (url) {
        this.videoUrlCache[video.id] = url;
      }
      
      return url;
    },
    // 更新视频元素的src属性
    updateVideoSrc() {
      if (!this.$refs.videoPlayer) {
        return;
      }
      
      const video = this.$refs.videoPlayer;
      const newSrc = this.getCachedVideoUrl(this.currentVideo);
      
      // 避免重复设置相同的src
      if (newSrc && newSrc !== this.currentVideoElementSrc) {
        video.src = newSrc;
        this.currentVideoElementSrc = newSrc;
      }
    },
    // 尝试播放视频，等待视频准备就绪
    attemptPlay() {
      if (!this.$refs.videoPlayer) {
        return;
      }
      const video = this.$refs.videoPlayer;
      
      // 确保src已设置
      if (!video.src) {
        this.updateVideoSrc();
      }
      
      if (this.preloadedVideo && this.preloadedVideo.readyState >= 4) {
        this.playVideo();
        return;
      }
      if (video.readyState >= 4) {
        this.playVideo();
      } else if (video.readyState >= 3) {
        this.playVideo();
      } else {
        // 等待视频加载足够数据
        const checkReady = () => {
          if (video.readyState >= 4) {
            this.playVideo();
          } else if (video.readyState >= 3 && this.playRetryCount >= 1) {
            this.playVideo();
          } else if (this.playRetryCount < this.maxRetryCount) {
            this.playRetryCount++;
            if (this.checkReadyTimer) clearTimeout(this.checkReadyTimer);
            this.checkReadyTimer = setTimeout(checkReady, 500);
          } else {
            logger.warn('视频加载超时，尝试直接播放');
            this.playVideo();
          }
        };
        checkReady();
      }
    },
    // 执行播放操作
    playVideo() {
      if (!this.$refs.videoPlayer) {
        return;
      }
      
      const video = this.$refs.videoPlayer;
      // 在真正播放前，尽量先应用断点（只对当前视频一次）
      this.applyResumeTimeIfNeeded();
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // 播放成功
            this.isPlaying = true;
            this.playRetryCount = 0;
            this.$emit('video-playing-changed', true);
          })
          .catch(error => {
            // 播放失败
            logger.warn('视频自动播放失败:', error);
            this.isPlaying = false;
            this.$emit('video-playing-changed', false);
            
            // 如果是 AbortError，可能是元素被移除，不重试
            if (error.name === 'AbortError') {
              logger.warn('视频播放被中断，可能是元素被移除');
              return;
            }
            
            // 其他错误，尝试重试
            if (this.playRetryCount < this.maxRetryCount) {
              this.playRetryCount++;
              if (this.retryPlayTimer) clearTimeout(this.retryPlayTimer);
              this.retryPlayTimer = setTimeout(() => {
                this.retryPlayTimer = null;
                this.attemptPlay();
              }, 1000);
            }
          });
      }
    },
    onVideoLoadStart() {
      // 不做任何操作，避免触发循环
    },
    onVideoLoaded() {
      // 视频元数据加载完成，尝试播放（但可能还不够流畅）
      // 注意：避免频繁触发，只在未播放时尝试
      
      if (!this.isPlaying && this.$refs.videoPlayer) {
        const video = this.$refs.videoPlayer;
        this.applyResumeTimeIfNeeded();
        // 如果视频已经可以播放，直接播放，不重复调用attemptPlay
        if (video.readyState >= 3) {
          this.playVideo();
        } else {
          this.attemptPlay();
        }
      }
    },
    onVideoCanPlayThrough() {
      // 视频可以完整播放，这是最佳的播放时机
      // 注意：避免频繁触发，只在未播放时尝试
      if (!this.isPlaying && this.$refs.videoPlayer) {
        this.applyResumeTimeIfNeeded();
        this.playVideo();
      }
    },
    onVideoTimeUpdate() {
      if (!this.$refs.videoPlayer) return;
      const video = this.$refs.videoPlayer;
      this.$emit('video-timeupdate', {
        currentTime: video.currentTime || 0,
        duration: video.duration || 0
      });
    },
    onVideoEnded() {
      // 自然播放到结尾时触发。
      // 根据不同模式采取不同策略：
      // - FIXED_WINDOW：页面停留时间由外部定时器控制，视频本身设置为 loop。
      //   在该模式下这里不主动通知父组件“结束”，让视频自行循环播放，
      //   避免在页面还没到切换时间时就被强制切走。
      // - PLAY_TO_END：需要在播完后通知父组件，由父组件决定是切下一个视频还是切到下一页。
      this.isPlaying = false;
      this.$emit('video-playing-changed', false);

      if (this.videoSwitchMode === 'PLAY_TO_END') {
        this.$emit('video-ended');
      }
    },
    onVideoError(error) {
      logger.error('视频加载失败:', error);
      // 视频加载失败时，也继续轮播
      this.isPlaying = false;
      this.$emit('video-playing-changed', false);
      this.$emit('video-ended');
    }
  }
};
</script>

<style scoped>
.fullscreen-video-container {
  width: 100%;
  height: 100%;
  background-image: url('@/assets/bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  overflow: hidden;
}

.video-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
}

.no-video-message {
  color: rgba(255, 255, 255, 0.7);
  font-size: 36px;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}
</style>
